import { redirect } from '@tanstack/react-router';
import type { SessionTenantType } from '@bliks-io/contracts';
import { useAuthStore } from '@/stores/auth-store';

export interface AuthSnapshot {
  isAuthenticated: boolean;
  slug: string;
  tenantType: SessionTenantType | null;
  isSuperAdmin: boolean;
  isPartner: boolean;
}

/**
 * Fresh auth facts read straight from the store. Route guards must use this
 * instead of the router-context snapshot: React flushes RouterProvider's
 * `context` prop a task AFTER `setAuth`, so a `navigate()` right after login
 * still sees stale auth in `context.auth`.
 */
export function getAuthSnapshot(): AuthSnapshot {
  const state = useAuthStore.getState();
  return {
    isAuthenticated: !!state.token,
    slug: state.slug(),
    tenantType: state.tenantType,
    isSuperAdmin: state.isSuperAdmin(),
    isPartner: state.isPartner()
  };
}

/**
 * The single unauthenticated-gate for every route guard. Tokenless means
 * logged out (or mid-logout teardown re-running guards): redirect immediately
 * — callers must NEVER fetch the profile without credentials. Deep-link
 * resume across this bounce was dropped with password auth (Release A
 * product call): a signed-out deep link lands on the default post-login
 * destination. Only step-up reauth threads a `returnTo` through the SSO
 * callback (stepUpReauth.ts) — wire the same round-trip through ssoStart if
 * deep-link resume ever comes back.
 */
export function requireAuthenticated(): AuthSnapshot {
  const auth = getAuthSnapshot();
  if (!auth.isAuthenticated) {
    throw redirect({ to: '/login' });
  }
  return auth;
}
